import { MouseEvent, useEffect, useRef } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@Utils';

interface CheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  id?: string;
}

const CHECKBOX_SIZES: Record<NonNullable<CheckboxProps['size']>, { box: string; rounded: string; glyph: number }> = {
  sm: { box: 'h-4 w-4', rounded: 'rounded', glyph: 11 },
  md: { box: 'h-[18px] w-[18px]', rounded: 'rounded-[5px]', glyph: 12 },
};

const Checkbox = ({
  checked,
  indeterminate = false,
  onChange,
  onClick,
  disabled = false,
  size = 'sm',
  id,
}: CheckboxProps) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const { box, rounded, glyph } = CHECKBOX_SIZES[size];

  return (
    <span className={cn('relative inline-flex shrink-0 items-center justify-center', box, disabled && 'opacity-40')}>
      <input
        ref={ref}
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'h-full w-full shrink-0 appearance-none border transition-colors',
          rounded,
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          checked || indeterminate ? 'border-brand-600 bg-brand-600' : 'border-gray-300 bg-white',
        )}
      />
      {checked && <Check size={glyph} strokeWidth={3} className="pointer-events-none absolute text-white" />}
      {indeterminate && !checked && (
        <Minus size={glyph} strokeWidth={3} className="pointer-events-none absolute text-white" />
      )}
    </span>
  );
};

export default Checkbox;
